import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Battery20 as BatteryLowIcon,
  Battery80 as BatteryGoodIcon,
  BatteryFull as BatteryFullIcon,
  History as HistoryIcon,
  Map as MapIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import TrackingMap from '../../components/TrackingMap';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';
const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const toRadians = (deg) => (deg * Math.PI) / 180;

const haversineDistanceKm = (lat1, lon1, lat2, lon2) => {
  if ([lat1, lon1, lat2, lon2].some((value) => Number.isNaN(parseFloat(value)))) {
    return 0;
  }
  const R = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const MIN_TIME_DELTA_HOURS = 5 / 3600;
const MAX_SEGMENT_DISTANCE_KM = 10;
const MAX_SEGMENT_SPEED = 120;

const enhanceHistoryWithSpeed = (points = []) => {
  if (!Array.isArray(points)) return [];

  return points.map((point, index) => {
    const existingSpeed = Number(point.speed);
    if (existingSpeed && Number.isFinite(existingSpeed)) {
      return { ...point, speed: parseFloat(existingSpeed.toFixed(2)) };
    }

    if (index === 0) {
      return { ...point, speed: 0 };
    }

    const prevPoint = points[index - 1];
    const prevTime = new Date(prevPoint.timestamp).getTime();
    const currentTime = new Date(point.timestamp).getTime();
    const hoursElapsed = (currentTime - prevTime) / (1000 * 60 * 60);

    if (!hoursElapsed || hoursElapsed < MIN_TIME_DELTA_HOURS) {
      return { ...point, speed: 0 };
    }

    const distanceKm = haversineDistanceKm(
      parseFloat(prevPoint.latitude),
      parseFloat(prevPoint.longitude),
      parseFloat(point.latitude),
      parseFloat(point.longitude)
    );

    if (!distanceKm || distanceKm <= 0 || distanceKm > MAX_SEGMENT_DISTANCE_KM) {
      return { ...point, speed: 0 };
    }

    const derivedSpeed = distanceKm / hoursElapsed;
    const safeSpeed = Math.min(Math.max(derivedSpeed, 0), MAX_SEGMENT_SPEED);

    return { ...point, speed: parseFloat(safeSpeed.toFixed(2)) };
  });
};

const getDefaultHistoryRange = () => {
  const now = new Date();
  const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
  const formatDate = (date) => date.toISOString().slice(0, 10);
  const formatTime = (date) => date.toTimeString().slice(0, 5);
  return {
    fromDate: formatDate(twoHoursAgo),
    fromTime: formatTime(twoHoursAgo),
    toDate: formatDate(now),
    toTime: formatTime(now)
  };
};

const LiveTracking = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [mapDialog, setMapDialog] = useState(false);
  const [mapStudent, setMapStudent] = useState(null);
  const [mapAddress, setMapAddress] = useState('');
  const [mapAddressLoading, setMapAddressLoading] = useState(false);
  const [historyRange, setHistoryRange] = useState(getDefaultHistoryRange());
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const intervalRef = useRef(null);

  useEffect(() => {
    loadLiveTrackingData();
    
    // Set up auto-refresh every 30 seconds
    intervalRef.current = setInterval(() => {
      loadLiveTrackingData();
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchMapAddress = async () => {
      if (!mapStudent || !mapStudent.latitude || !mapStudent.longitude) {
        setMapAddress(mapStudent?.address || '');
        setMapAddressLoading(false);
        return;
      }

      if (!GOOGLE_MAP_KEY) {
        setMapAddress(mapStudent.address || '');
        setMapAddressLoading(false);
        return;
      }

      setMapAddressLoading(true);
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${mapStudent.latitude},${mapStudent.longitude}&key=${GOOGLE_MAP_KEY}`
        );
        const data = await response.json();
        const formatted =
          data?.status === 'OK' && data?.results?.length
            ? data.results[0].formatted_address
            : mapStudent.address || '';
        setMapAddress(formatted);
      } catch (error) {
        console.error('Error fetching map address:', error);
        setMapAddress(mapStudent.address || '');
      } finally {
        setMapAddressLoading(false);
      }
    };

    if (mapDialog) {
      fetchMapAddress();
    } else {
      setMapAddress('');
      setMapAddressLoading(false);
    }
  }, [mapDialog, mapStudent]);

  useEffect(() => {
    // Filter students based on search term
    const filtered = students.filter(student =>
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.roll_number?.toString().includes(searchTerm) ||
      `${student.standard}${student.division}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStudents(filtered);
  }, [students, searchTerm]);

  const loadLiveTrackingData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/school/live-tracking`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      } else {
        console.error('Failed to load tracking data');
        setStudents([]);
      }
    } catch (error) {
      console.error('Error loading tracking data:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const buildDateTimeString = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const safeTime = timeStr && timeStr.length >= 5 ? timeStr : '00:00';
    return `${dateStr} ${safeTime}:00`;
  };

  const fetchLocationHistory = async () => {
    if (!selectedStudent) return;

    const startRange = buildDateTimeString(historyRange.fromDate, historyRange.fromTime);
    const endRange = buildDateTimeString(historyRange.toDate, historyRange.toTime);

    if (!startRange || !endRange) {
      setHistoryError('Please select both start and end date & time.');
      return;
    }

    if (new Date(startRange) >= new Date(endRange)) {
      setHistoryError('End time must be after start time.');
      return;
    }

    setHistoryLoading(true);
    setHistoryError('');

    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        start: startRange,
        end: endRange
      });
      const response = await fetch(
        `${API_BASE_URL}/school/students/${selectedStudent.student_id}/location-history-range?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load history');
      }

      const data = await response.json();
      const historyArray = Array.isArray(data) ? data : [];
      setHistoryData(enhanceHistoryWithSpeed(historyArray));

      if (!historyArray || historyArray.length === 0) {
        setHistoryError('No history found for the selected range.');
      }
    } catch (error) {
      console.error('Error loading location history:', error);
      setHistoryData([]);
      setHistoryError('Error loading location history. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistoryDialog = (student) => {
    setSelectedStudent(student);
    setHistoryRange(getDefaultHistoryRange());
    setHistoryData([]);
    setHistoryError('');
    setHistoryDialog(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialog(false);
    setHistoryData([]);
    setHistoryError('');
    setHistoryLoading(false);
  };

  const getBatteryIcon = (batteryLevel) => {
    if (batteryLevel >= 80) return <BatteryFullIcon color="success" />;
    if (batteryLevel >= 30) return <BatteryGoodIcon color="warning" />;
    return <BatteryLowIcon color="error" />;
  };



  const getLocationStatus = (minutesAgo) => {
    if (minutesAgo === null) return { text: 'No Data', color: 'default' };
    if (minutesAgo <= 5) return { text: 'Live', color: 'success' };
    if (minutesAgo <= 30) return { text: `${minutesAgo}m ago`, color: 'warning' };
    return { text: `${minutesAgo}m ago`, color: 'error' };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'No data';
    return new Date(timestamp).toLocaleString();
  };

  const StudentCard = ({ student }) => {
    const locationStatus = getLocationStatus(student.minutes_ago);
    
    return (
      <Card sx={{ p: 2, height: '100%', position: 'relative' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              {student.first_name?.[0]}{student.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {student.first_name} {student.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {student.standard}-{student.division} | Roll: {student.roll_number}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={locationStatus.text}
            color={locationStatus.color}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <LocationIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="textSecondary">
              Last Location:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
            {student.address || `${student.latitude}, ${student.longitude}` || 'No location data'}
          </Typography>
          {student.last_location_update && (
            <Typography variant="caption" color="textSecondary" sx={{ ml: 3, display: 'block' }}>
              🕒 {formatTimestamp(student.last_location_update)}
            </Typography>
          )}
        </Box>

        {student.device_id && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              {getBatteryIcon(student.battery_level)}
              <Typography variant="body2">
                {student.battery_level}%
              </Typography>
            </Box>
            <Chip
              label={student.device_status || 'Unknown'}
              color={student.device_status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        )}

        <Box display="flex" gap={1} mt={2}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => {
              handleOpenHistoryDialog(student);
            }}
            disabled={!student.device_id}
          >
            History
          </Button>
          <Button
            size="small"
            startIcon={<MapIcon />}
            disabled={!student.latitude || !student.longitude}
            onClick={() => {
              if (student.latitude && student.longitude) {
                setMapStudent(student);
                setMapDialog(true);
              }
            }}
          >
            Map
          </Button>
        </Box>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Live Tracking
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Real-time location monitoring of all students
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadLiveTrackingData}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Stats */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, roll number, or class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Chip
                label={`Total: ${students.length}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Active: ${students.filter(s => s.device_status === 'active').length}`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`Low Battery: ${students.filter(s => s.battery_level < 30).length}`}
                color="error"
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Students Grid */}
      {filteredStudents.length === 0 ? (
        <Alert severity="info">
          {students.length === 0 ? 'No students found.' : 'No students match your search criteria.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredStudents.map((student) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={student.student_id}>
              <StudentCard student={student} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Map Dialog */}
      <Dialog
        open={mapDialog}
        onClose={() => setMapDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Location Map - {mapStudent?.first_name} {mapStudent?.last_name}
          <IconButton
            onClick={() => setMapDialog(false)}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {mapStudent && (
            <Box sx={{ height: 500, width: '100%', position: 'relative' }}>
              {mapStudent.latitude && mapStudent.longitude ? (
                GOOGLE_MAP_KEY ? (
                  <iframe
                    src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAP_KEY}&q=${Number(mapStudent.latitude)},${Number(mapStudent.longitude)}&zoom=17&maptype=roadmap`}
                    width="100%"
                    height="100%"
                    style={{ border: 'none', borderRadius: '8px' }}
                    title={`Map for ${mapStudent.first_name} ${mapStudent.last_name}`}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <iframe
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(mapStudent.longitude) - 0.01},${Number(mapStudent.latitude) - 0.01},${Number(mapStudent.longitude) + 0.01},${Number(mapStudent.latitude) + 0.01}&layer=mapnik&marker=${Number(mapStudent.latitude)},${Number(mapStudent.longitude)}`}
                    width="100%"
                    height="100%"
                    style={{ border: 'none', borderRadius: '8px' }}
                    title={`Map for ${mapStudent.first_name} ${mapStudent.last_name}`}
                  />
                )
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  Location not available
                </Box>
              )}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>📍 Address:</strong>{' '}
                  {mapAddressLoading
                    ? 'Fetching precise address…'
                    : mapAddress || mapStudent.address || 'No address available'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>🌐 Coordinates:</strong> {mapStudent.latitude}, {mapStudent.longitude}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>🔋 Battery:</strong> {mapStudent.battery_level}%
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                  <strong>🕒 Last Updated:</strong> {formatTimestamp(mapStudent.last_location_update)}
                </Typography>
                {mapStudent.minutes_ago !== null && (
                  <Typography variant="body2" color={mapStudent.minutes_ago <= 5 ? 'success.main' : mapStudent.minutes_ago <= 30 ? 'warning.main' : 'error.main'}>
                    <strong>⏱️ Status:</strong> {mapStudent.minutes_ago <= 5 ? 'Live Location' : `${mapStudent.minutes_ago} minutes ago`}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Location History Dialog */}
      <Dialog
        open={historyDialog}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Location History - {selectedStudent?.first_name} {selectedStudent?.last_name}
          <IconButton
            onClick={handleCloseHistoryDialog}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="subtitle2" color="textSecondary">
              Select a date & time range to view history
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2" fontWeight={600}>
                    From
                  </Typography>
                  <TextField
                    type="date"
                    label="Date"
                    value={historyRange.fromDate}
                    onChange={(e) => setHistoryRange((prev) => ({ ...prev, fromDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="time"
                    label="Time"
                    value={historyRange.fromTime}
                    onChange={(e) => setHistoryRange((prev) => ({ ...prev, fromTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2" fontWeight={600}>
                    To
                  </Typography>
                  <TextField
                    type="date"
                    label="Date"
                    value={historyRange.toDate}
                    onChange={(e) => setHistoryRange((prev) => ({ ...prev, toDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="time"
                    label="Time"
                    value={historyRange.toTime}
                    onChange={(e) => setHistoryRange((prev) => ({ ...prev, toTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Grid>
            </Grid>
            {historyError && <Alert severity="warning">{historyError}</Alert>}
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button onClick={handleCloseHistoryDialog} color="inherit">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={fetchLocationHistory}
                disabled={historyLoading}
              >
                {historyLoading ? 'Loading…' : 'Load History'}
              </Button>
            </Box>
          </Box>
          <Box mt={3}>
            {historyLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
              </Box>
            ) : historyData.length > 0 ? (
              <>
                <Box sx={{ height: 400, mb: 3 }}>
                  <TrackingMap historyData={historyData} />
                </Box>
                <List>
                  {historyData.map((location, index) => (
                    <React.Fragment key={`${location.timestamp}-${index}`}>
                      <ListItem alignItems="flex-start" secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => {
                            setMapStudent({
                              ...selectedStudent,
                              latitude: location.latitude,
                              longitude: location.longitude,
                              address: location.address,
                              battery_level: location.battery_level
                            });
                            setMapDialog(true);
                          }}
                        >
                          <MapIcon />
                        </IconButton>
                      }>
                        <ListItemText
                          primary={location.address || `${location.latitude}, ${location.longitude}`}
                          secondary={
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <Typography variant="body2" color="textSecondary">
                                {formatTimestamp(location.timestamp)}
                              </Typography>
                              {location.battery_level && (
                                <Typography variant="body2" color="textSecondary">
                                  Battery: {location.battery_level}%
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < historyData.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </>
            ) : (
              <Alert severity="info">Choose a range and tap “Load History” to see past locations.</Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LiveTracking;